import React, { useRef, useEffect, useCallback, useState } from "react";

const isGoogleMapsLoaded = () =>
    !!(window.google && window.google.maps && window.google.maps.places);

const AutoComplete = ({
    onAddressSelect,
    name,
    id,
    placeholder,
    latitude,
    longitude,
    value,
    country,
    state,
    city,
}) => {
    const autoCompleteRef = useRef(null);
    const inputRef = useRef(null);
    const [inputValue, setInputValue] = useState(value || "");

    const initAutocomplete = useCallback(() => {
        if (!isGoogleMapsLoaded() || !inputRef.current) return;
        if (autoCompleteRef.current) return;

        autoCompleteRef.current = new window.google.maps.places.Autocomplete(
            inputRef.current,
            { types: ["establishment"] }
        );

        autoCompleteRef.current.addListener("place_changed", () => {
            const place = autoCompleteRef.current.getPlace();

            if (place?.formatted_address) {
                onAddressSelect(place.formatted_address);
                setInputValue(place.formatted_address);

                const { lat, lng } = place.geometry?.location ?? {};
                if (lat && lng) {
                    latitude(lat());
                    longitude(lng());
                }

                if (place.address_components) {
                    const addressComponents = place.address_components;
                    const countryObj = addressComponents.find(
                        (component) => component.types.includes("country")
                    );
                    const stateObj = addressComponents.find(
                        (component) => component.types.includes("administrative_area_level_1")
                    );
                    const cityObj = addressComponents.find(
                        (component) => component.types.includes("locality") || component.types.includes("administrative_area_level_2")
                    );

                    if (countryObj) country(countryObj.long_name);
                    if (stateObj) state(stateObj.long_name);
                    if (cityObj) city(cityObj.long_name);
                }
            }
        });
    }, [onAddressSelect, latitude, longitude, country, state, city]);

    // Wait for Google Maps to be loaded by GoogleMapComponent (useLoadScript)
    // Do NOT load the script ourselves — let useLoadScript handle it
    useEffect(() => {
        if (isGoogleMapsLoaded()) {
            initAutocomplete();
            return;
        }

        const interval = setInterval(() => {
            if (isGoogleMapsLoaded()) {
                clearInterval(interval);
                initAutocomplete();
            }
        }, 200);

        return () => clearInterval(interval);
    }, [initAutocomplete]);

    useEffect(() => {
        setInputValue(value || "");
    }, [value]);

    return (
        <div>
            <input
                type="text"
                autoComplete="off"
                name={name}
                id={id}
                value={inputValue}
                placeholder={placeholder}
                className="form-control"
                ref={inputRef}
                onChange={(e) => setInputValue(e.target.value)}
                required
            />
        </div>
    );
};

export default AutoComplete;
